// Line glyphs (the mockups' engraved-line icons), currentColor, 24-unit boxes.
import type { SVGProps } from "react";

type P = SVGProps<SVGSVGElement>;
const base = (p: P): P => ({
  viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, ...p,
});

export const IconBell = (p: P) => (
  <svg {...base(p)}>
    <path d="M6 17c1.2-1.6 1.6-3 1.6-5.6 0-2.8 1.9-5 4.4-5s4.4 2.2 4.4 5c0 2.6.4 4 1.6 5.6Z" />
    <path d="M10 19.5c.6 1 1.3 1.4 2 1.4s1.4-.4 2-1.4" />
    <circle cx="12" cy="4.6" r=".9" fill="currentColor" stroke="none" />
  </svg>
);
export const IconMenu = (p: P) => (
  <svg {...base({ strokeWidth: 2.2, ...p })}>
    <path d="M5 7h14M5 12h14M5 17h14" />
  </svg>
);
export const IconGlobe = (p: P) => (
  <svg {...base({ strokeWidth: 1.5, ...p })}>
    <circle cx="12" cy="12" r="9" />
    <ellipse cx="12" cy="12" rx="4" ry="9" />
    <path d="M3 12h18M4.5 7.5q7.5 2.5 15 0M4.5 16.5q7.5-2.5 15 0" />
  </svg>
);
export const IconChevronDown = (p: P) => (
  <svg {...base({ strokeWidth: 2, ...p })}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);
export const IconChevronUp = (p: P) => (
  <svg {...base({ strokeWidth: 2, ...p })}>
    <path d="M6 15l6-6 6 6" />
  </svg>
);
export const IconChevronRight = (p: P) => (
  <svg {...base({ strokeWidth: 2, ...p })}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);
export const IconChevronLeft = (p: P) => (
  <svg {...base({ strokeWidth: 2, ...p })}>
    <path d="M15 6l-6 6 6 6" />
  </svg>
);
export const IconClose = (p: P) => (
  <svg {...base({ strokeWidth: 2, ...p })}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
export const IconGear = (p: P) => (
  <svg {...base(p)}>
    <path d="M12 3.5l1.6.4.5 1.9 1.7.7 1.7-1 1.3 1.1-1 1.7.7 1.7 1.9.5.4 1.6-.4 1.6-1.9.5-.7 1.7 1 1.7-1.1 1.3-1.7-1-1.7.7-.5 1.9-1.6.4-1.6-.4-.5-1.9-1.7-.7-1.7 1-1.3-1.1 1-1.7-.7-1.7-1.9-.5L3.5 12l.4-1.6 1.9-.5.7-1.7-1-1.7 1.1-1.3 1.7 1 1.7-.7.5-1.9z" />
    <circle cx="12" cy="12" r="2.8" />
  </svg>
);
export const IconSearch = (p: P) => (
  <svg {...base(p)}>
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="M15 15l5 5" />
  </svg>
);
export const IconHourglass = (p: P) => (
  <svg {...base(p)}>
    <path d="M7 3.5h10M7 20.5h10M8.5 3.5c0 5 3.5 6.5 3.5 8.5s-3.5 3.5-3.5 8.5M15.5 3.5c0 5-3.5 6.5-3.5 8.5s3.5 3.5 3.5 8.5" />
    <path d="M10 19.5q2-3 4 0z" fill="currentColor" stroke="none" opacity=".8" />
  </svg>
);
export const IconSwords = (p: P) => (
  <svg {...base(p)}>
    <path d="M4 4l11 11M20 4L9 15M14 14l4 4M10 14l-4 4M17 18l1.5 1.5M7 18l-1.5 1.5" />
  </svg>
);
/** Two crossed swords with guards and pommels — reads as swords even at badge size (incoming attacks, Oct 14 2026). */
export const IconCrossedSwords = (p: P) => (
  <svg {...base({ strokeWidth: 2.2, ...p })}>
    <path d="M4 4l10.5 10.5M20 4L9.5 14.5M12 17l5-5M7 12l5 5M15.5 15.5l3 3M8.5 15.5l-3 3" />
    <circle cx="19.6" cy="19.6" r="1.3" fill="currentColor" />
    <circle cx="4.4" cy="19.6" r="1.3" fill="currentColor" />
  </svg>
);
export const IconShield = (p: P) => (
  <svg {...base(p)}>
    <path d="M12 3l7 3v6c0 4-3 7.5-7 9-4-1.5-7-5-7-9V6z" />
    <path d="M12 8v6M9 11h6" />
  </svg>
);
export const IconStar = (p: P) => (
  <svg {...base(p)}>
    <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.9l-5.3 2.8 1.1-5.9-4.3-4.1 5.9-.8z" />
  </svg>
);
export const IconHammer = (p: P) => (
  <svg {...base(p)}>
    <path d="M13 7l4 4M3 20l9-9M12 4h7l1 3-3 3-4-1z" />
  </svg>
);
export const IconEye = (p: P) => (
  <svg {...base(p)}>
    <path d="M2.5 12q4.5-6.5 9.5-6.5t9.5 6.5q-4.5 6.5-9.5 6.5T2.5 12z" />
    <circle cx="12" cy="12" r="2.8" />
  </svg>
);
export const IconPerson = (p: P) => (
  <svg {...base(p)}>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 20c1-4 3.5-6 7-6s6 2 7 6" />
  </svg>
);
export const IconPeople = (p: P) => (
  <svg {...base(p)}>
    <circle cx="9" cy="8.5" r="3" />
    <circle cx="16.5" cy="9.5" r="2.4" />
    <path d="M3.5 19c.8-3.4 2.8-5 5.5-5s4.7 1.6 5.5 5M14.5 18.5c.6-2.4 1.8-3.5 3.5-3.5 1.4 0 2.3.9 2.5 3.5" />
  </svg>
);
export const IconBookmark = (p: P) => (
  <svg {...base(p)}>
    <path d="M7 3.5h10v17l-5-3.5-5 3.5z" />
  </svg>
);
export const IconMail = (p: P) => (
  <svg {...base(p)}>
    <rect x="3" y="5.5" width="18" height="13" rx="1.5" />
    <path d="M3.5 7l8.5 6 8.5-6" />
  </svg>
);
export const IconScroll = (p: P) => (
  <svg {...base(p)}>
    <path d="M7 4h11a2 2 0 012 2v12a2 2 0 01-2 2H7M7 4a2 2 0 00-2 2v12a2 2 0 002 2M7 4v16M10 9h6M10 13h6" />
  </svg>
);
export const IconCrown = (p: P) => (
  <svg {...base(p)}>
    <path d="M4 17h16l1-9-5 3.5L12 5l-4 6.5L3 8z" />
    <path d="M4 20h16" />
  </svg>
);
export const IconTrophy = (p: P) => (
  <svg {...base(p)}>
    <path d="M8 4h8v5a4 4 0 01-8 0zM8 6H5v1.5A3.5 3.5 0 008.5 11M16 6h3v1.5a3.5 3.5 0 01-3.5 3.5M12 13v4M8 20h8M9 17h6" />
  </svg>
);
export const IconMap = (p: P) => (
  <svg {...base(p)}>
    <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2zM9 4v14M15 6v14" />
  </svg>
);
export const IconHelp = (p: P) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 015 0c0 1.8-2.5 2-2.5 4M12 17.5v.01" />
  </svg>
);
export const IconLogout = (p: P) => (
  <svg {...base(p)}>
    <path d="M10 4H5v16h5M14 16l4-4-4-4M18 12H9" />
  </svg>
);
export const IconCheck = (p: P) => (
  <svg {...base({ strokeWidth: 2.4, ...p })}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);
export const IconPlus = (p: P) => (
  <svg {...base({ strokeWidth: 2.2, ...p })}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);
export const IconMinus = (p: P) => (
  <svg {...base({ strokeWidth: 2.2, ...p })}>
    <path d="M5 12h14" />
  </svg>
);
export const IconInfo = (p: P) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5.5M12 8v.2" />
  </svg>
);
export const IconArrowUp = (p: P) => (
  <svg {...base(p)}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
);
export const IconExchange = (p: P) => (
  <svg {...base(p)}>
    <path d="M4 8h13l-3-3M20 16H7l3 3" />
  </svg>
);
export const IconServer = (p: P) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);
export const IconKeep = (p: P) => (
  <svg {...base(p)}>
    <path d="M4 21V9l3-2 3 2v3h4V9l3-2 3 2v12zM10 21v-5h4v5" />
  </svg>
);
export const IconCoins = (p: P) => (
  <svg {...base(p)}>
    <ellipse cx="12" cy="7" rx="7" ry="3" />
    <path d="M5 7v10c0 1.7 3.1 3 7 3s7-1.3 7-3V7M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" />
  </svg>
);
export const IconDiamond = (p: P) => (
  <svg {...base(p)}>
    <path d="M12 4l8 8-8 8-8-8z" fill="currentColor" stroke="none" />
  </svg>
);
export const IconArrowRight = (p: P) => (
  <svg {...base({ strokeWidth: 2, ...p })}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
export const IconQuill = (p: P) => (
  <svg {...base(p)}>
    <path d="M20 4c-6 0-11 3-13 9l-3 7 7-3c6-2 9-7 9-13zM7 17l6-6" />
  </svg>
);
export const IconTrash = (p: P) => (
  <svg {...base(p)}>
    <path d="M5 7h14M9 7V4.5h6V7M7 7l1 13h8l1-13M10 11v6M14 11v6" />
  </svg>
);
export const IconCrosshair = (p: P) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="7" />
    <path d="M12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
  </svg>
);
export const IconLayers = (p: P) => (
  <svg {...base(p)}>
    <path d="M12 4l9 5-9 5-9-5zM3 14l9 5 9-5" />
  </svg>
);
export const IconWheel = (p: P) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="2" />
    <path d="M12 3.5v6M12 14.5v6M3.5 12h6M14.5 12h6M6 6l4.2 4.2M13.8 13.8L18 18M18 6l-4.2 4.2M10.2 13.8L6 18" />
  </svg>
);
export const IconHeart = (p: P) => (
  <svg {...base(p)}>
    <path d="M12 20s-7.5-4.6-7.5-10A4 4 0 0112 7.5 4 4 0 0119.5 10c0 5.4-7.5 10-7.5 10z" />
  </svg>
);
export const IconFlag = (p: P) => (
  <svg {...base(p)}>
    <path d="M5 21V4M5 4h12l-3 4 3 4H5" />
  </svg>
);
export const IconClock = (p: P) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7v5.5l3.5 2" />
  </svg>
);
export const IconLock = (p: P) => (
  <svg {...base(p)}>
    <rect x="5.5" y="10.5" width="13" height="10" rx="1.5" />
    <path d="M8.5 10.5V7.5a3.5 3.5 0 017 0v3" />
  </svg>
);
export const IconLoginDoor = (p: P) => (
  <svg {...base(p)}>
    <path d="M14 4h5v16h-5M3 12h11M10 8l4 4-4 4" />
  </svg>
);

/** The scroll-flourish beside a screen title (valor_ui.flourish). `mirror` for the left side. */
export const Flourish = ({ mirror = false, ...p }: P & { mirror?: boolean }) => (
  <svg viewBox="-2 -10 66 20" aria-hidden="true" {...p}>
    <g transform={mirror ? "translate(62 0) scale(-1 1)" : undefined}>
      <path d="M0 0 C10 -7 18 -7 26 0 C34 7 42 7 54 0" fill="none" stroke="#AD8445" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M14 -3 q-6 -6 -12 -1 M40 3 q6 6 12 1" fill="none" stroke="#EDD69D" strokeWidth="1.1" strokeLinecap="round" opacity=".9" />
      <rect x="57" y="-3" width="6" height="6" transform="rotate(45 60 0)" fill="#EDD69D" />
      <circle cx="26" cy="0" r="1.8" fill="#EDD69D" />
    </g>
  </svg>
);

/** The crimson pennant under the avatar, with the gold crest. */
export const Pennant = (p: P) => (
  <svg viewBox="0 0 58 84" aria-hidden="true" {...p}>
    <defs>
      <linearGradient id="pn-crim" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#B84C37" />
        <stop offset=".5" stopColor="#7A2A20" />
        <stop offset="1" stopColor="#501614" />
      </linearGradient>
    </defs>
    <path d="M1 1H57V67L29 83L1 67Z" fill="url(#pn-crim)" stroke="#AD8445" strokeWidth="1.5" />
    <path d="M7 7H51V63L29 75L7 63Z" fill="none" stroke="#C99A5A" strokeWidth=".8" opacity=".6" />
    <path d="M29 39l6 12-6 12-6-12Z M17 51h24" fill="none" stroke="#BD9F6F" strokeWidth="1.4" />
  </svg>
);
export const IconReply = (p: P) => (
  <svg {...base(p)}>
    <path d="M9 7 4 11.5 9 16" />
    <path d="M4 11.5h9.5a6 6 0 0 1 6 6V19" />
  </svg>
);
export const IconSend = (p: P) => (
  <svg {...base(p)}>
    <path d="M20 4 4 10.5l7 2.5 2.5 7L20 4Z" />
    <path d="M11 13l9-9" />
  </svg>
);
export const IconDoor = (p: P) => (
  <svg {...base(p)}>
    <path d="M6 3h12v18H6z" />
    <path d="M14 12h.01" />
    <path d="M6 21h12" />
  </svg>
);
export const IconSkull = (p: P) => (
  <svg {...base(p)}>
    <path d="M12 3a7 7 0 0 0-7 7c0 2.4 1.2 4.2 3 5.3V19h8v-3.7c1.8-1.1 3-2.9 3-5.3a7 7 0 0 0-7-7Z" />
    <circle cx="9.5" cy="10.5" r="1.3" />
    <circle cx="14.5" cy="10.5" r="1.3" />
    <path d="M10 19v2M14 19v2" />
  </svg>
);
export const IconHandshake = (p: P) => (
  <svg {...base(p)}>
    <path d="M3 10l4-4 4 3 2-2 4 1 4 3" />
    <path d="M3 10l5 6 3 2 4-1 3-3 3-4" />
    <path d="M11 13l2 2" />
  </svg>
);
export const IconScales = (p: P) => (
  <svg {...base(p)}>
    <path d="M12 3v18M5 21h14" />
    <path d="M4 7h16" />
    <path d="M7 7l-3 7a3 3 0 0 0 6 0L7 7ZM17 7l-3 7a3 3 0 0 0 6 0l-3-7Z" />
  </svg>
);
export const IconFilter = (p: P) => (
  <svg {...base(p)}>
    <path d="M4 5h16l-6 7v6l-4 2v-8L4 5Z" />
  </svg>
);
export const IconPencil = (p: P) => (
  <svg {...base(p)}>
    <path d="M4 20l1.2-4.6L16.5 4.1a1.6 1.6 0 0 1 2.3 0l1.1 1.1a1.6 1.6 0 0 1 0 2.3L8.6 18.8 4 20zM14.5 6l3.5 3.5" />
  </svg>
);
/** the whole city: four corner brackets (the city view's back-to-1x button) */
export const IconFit = (p: P) => (
  <svg {...base({ strokeWidth: 2, ...p })}>
    <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
  </svg>
);
/** Share to Discord (Oct 13, 2026): a speech bubble with an arrow out of it (a plain glyph, not Discord's own mark). */
export const IconShareOut = (p: P) => (
  <svg {...base(p)}>
    <path d="M13 4.5H6.5a2.5 2.5 0 0 0-2.5 2.5v7a2.5 2.5 0 0 0 2.5 2.5H8v3.2l3.8-3.2h5.7a2.5 2.5 0 0 0 2.5-2.5V11" />
    <path d="M15.5 3.5H20.5V8.5" />
    <path d="M20.5 3.5 13.5 10.5" />
  </svg>
);
export const IconCart = (p: P) => (
  <svg {...base(p)}>
    <path d="M3 8h13l-1.6 6.5H4.8z" />
    <path d="M16 8l1.5-3H21M6.5 8l2-3.4M11 8l1.2-3.4" />
    <circle cx="7" cy="18.5" r="2" />
    <circle cx="13" cy="18.5" r="2" />
  </svg>
);
